import { inject, Injectable } from '@angular/core';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { InsuranceCustomerClientService } from '../../client';
import { InsurancesClientService } from '../../client/insurances-client.service';
import { InsuranceCustomerMapper } from '../../mapper';
import { InsurancePlanMapper } from '../../mapper/insurance-plan.mapper';
import { InsuranceCustomer } from '../../model/insurance/insurance-customer.model';
import { InsurancePlan } from '../../model/insurance/insurance-plan.model';
import { InsuranceStateModel } from './insurance-state.model';
import { ClearSelectedInsurancePlan, LoadInsuranceCustomer, LoadInsurancePlans, SelectInsurancePlan } from './insurance.actions';

const INSURANCE_STATE_TOKEN = new StateToken<InsuranceStateModel>('InsuranceState');

const DEFAULT_STATE: InsuranceStateModel = {
    insurancePlans: { state: ObjectState.UNINIT, value: null },
    selectedInsurancePlan: null,
    insuranceCustomer: { state: ObjectState.UNINIT, value: null },
};

@State<InsuranceStateModel>({
    name: INSURANCE_STATE_TOKEN,
    defaults: DEFAULT_STATE,
})
@Injectable()
export class InsuranceState {
    private readonly insurancesClientService = inject(InsurancesClientService);
    private readonly insurancePlanMapper = inject(InsurancePlanMapper);
    private readonly insuranceCustomerClientService = inject(InsuranceCustomerClientService);
    private readonly insuranceCustomerMapper = inject(InsuranceCustomerMapper);

    @Selector([INSURANCE_STATE_TOKEN])
    static insurancePlans$(state: InsuranceStateModel): StatefullValue<InsurancePlan[] | null> {
        return state.insurancePlans;
    }

    @Selector([INSURANCE_STATE_TOKEN])
    static selectedInsurancePlan(state: InsuranceStateModel): InsurancePlan | null {
        return state.selectedInsurancePlan;
    }

    @Selector([INSURANCE_STATE_TOKEN])
    static insuranceCustomer(state: InsuranceStateModel): StatefullValue<InsuranceCustomer | null> {
        return state.insuranceCustomer;
    }

    @Action(LoadInsurancePlans)
    loadInsurancePlans(ctx: StateContext<InsuranceStateModel>): Observable<InsurancePlan[]> {
        ctx.patchState({
            insurancePlans: { state: ObjectState.LOADING, value: null },
        });

        return this.insurancesClientService.getInsurancePlans().pipe(
            tap(insurancePlanDtos => {
                const insurancePlans = insurancePlanDtos.map(dto => this.insurancePlanMapper.mapDtoToModel(dto));
                ctx.patchState({
                    insurancePlans: { state: ObjectState.VALID, value: insurancePlans },
                });
            }),
            catchError(error => {
                ctx.patchState({
                    insurancePlans: { state: ObjectState.ERROR, value: null },
                });
                return throwError(() => error);
            }),
        );
    }

    @Action(SelectInsurancePlan)
    selectInsurancePlan(ctx: StateContext<InsuranceStateModel>, action: SelectInsurancePlan): void {
        ctx.patchState({
            selectedInsurancePlan: action.insurancePlan,
        });
    }

    @Action(ClearSelectedInsurancePlan)
    clearSelectedInsurancePlan(ctx: StateContext<InsuranceStateModel>): void {
        ctx.patchState({
            selectedInsurancePlan: null,
        });
    }

    @Action(LoadInsuranceCustomer)
    loadInsuranceCustomer(ctx: StateContext<InsuranceStateModel>, action: LoadInsuranceCustomer): Observable<InsuranceCustomer> {
        ctx.patchState({
            insuranceCustomer: { state: ObjectState.LOADING, value: null },
        });
        return this.insuranceCustomerClientService.getCustomer(action.customerId).pipe(
            map(dto => this.insuranceCustomerMapper.mapDtoToModel(dto)),
            tap(insuranceCustomer => {
                ctx.patchState({
                    insuranceCustomer: { state: ObjectState.VALID, value: insuranceCustomer },
                });
            }),
            catchError(error => {
                ctx.patchState({
                    insuranceCustomer: { state: ObjectState.ERROR, value: null },
                });
                return throwError(() => error);
            }),
        );
    }
}
